/**
 * Popup 功能模組
 * 包含影片 popup、校友 popup、Pathway popup 等功能
 */

// ============================================
// YouTube 封面圖自動抓取功能
// ============================================

/**
 * 從 YouTube URL 提取視頻 ID
 * @param {string} url - YouTube URL
 * @returns {string|null} - 視頻 ID 或 null
 */
function extractYouTubeVideoId(url) {
    if (!url) return null;

    let videoId = null;

    // 處理各種 YouTube URL 格式
    if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('v=')[1];
    } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
        videoId = url.split('embed/')[1];
    }

    // 移除額外的參數
    if (videoId && videoId.includes('&')) {
        videoId = videoId.split('&')[0];
    }

    // 移除時間參數
    if (videoId && videoId.includes('?')) {
        videoId = videoId.split('?')[0];
    }

    return videoId;
}

/**
 * 獲取 YouTube 視頻縮圖 URL
 * @param {string} videoId - YouTube 視頻 ID
 * @param {string} quality - 圖片質量 (maxresdefault, sddefault, hqdefault, mqdefault, default)
 * @returns {string} - 縮圖 URL
 */
function getYouTubeThumbnailUrl(videoId, quality = 'maxresdefault') {
    if (!videoId) return '';
    return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

/**
 * 自動設置 Kadence Video Popup 的 YouTube 封面圖
 * 會按照質量順序嘗試，直到找到可用的封面圖
 * 順序：maxresdefault → sddefault → hqdefault → mqdefault → default
 */
function autoSetYouTubeThumbnails() {
    // 找到所有 Kadence video popup
    const videoPopups = document.querySelectorAll('.kadence-video-popup-wrap');

    videoPopups.forEach((popup) => {
        // 獲取 YouTube 連結
        const link = popup.querySelector('a.kadence-video-popup-link');
        if (!link) return;

        const youtubeUrl = link.getAttribute('href');
        if (!youtubeUrl) return;

        // 提取視頻 ID
        const videoId = extractYouTubeVideoId(youtubeUrl);
        if (!videoId) return;

        // 獲取封面圖元素
        const posterImg = popup.querySelector('img.kadence-video-poster');
        if (!posterImg) return;

        // 定義質量降級順序
        // maxresdefault: 1280x720 (最高質量，但不是所有視頻都有)
        // sddefault: 640x480 (標準質量)
        // hqdefault: 480x360 (高質量，幾乎所有視頻都有)
        // mqdefault: 320x180 (中等質量)
        // default: 120x90 (最低質量，所有視頻都有，作為最後的保底)
        const qualities = ['maxresdefault', 'sddefault', 'hqdefault', 'mqdefault', 'default'];
        let currentQualityIndex = 0;

        // 遞迴函數來嘗試不同質量
        function tryLoadThumbnail(qualityIndex) {
            if (qualityIndex >= qualities.length) {
                console.warn('⚠️ 無法載入任何質量的 YouTube 封面圖:', videoId);
                return;
            }

            const quality = qualities[qualityIndex];
            const thumbnailUrl = getYouTubeThumbnailUrl(videoId, quality);
            const testImg = new Image();

            // 定義每個質量預期的最小尺寸
            const minDimensions = {
                maxresdefault: { width: 1280, height: 720 },
                sddefault: { width: 640, height: 480 },
                hqdefault: { width: 480, height: 360 },
                mqdefault: { width: 320, height: 180 },
                default: { width: 120, height: 90 },
            };

            testImg.onload = function () {
                // 檢查圖片尺寸是否符合預期
                const expectedDim = minDimensions[quality];
                const isValidSize = testImg.width >= expectedDim.width && testImg.height >= expectedDim.height;

                // 如果圖片太小（可能是佔位圖或錯誤圖片），視為失敗並嘗試下一個質量
                if (!isValidSize && quality !== 'default') {
                    // console.log(`⏭️ ${quality} 尺寸不符（${testImg.width}x${testImg.height}），嘗試下一個質量... (${videoId})`);
                    tryLoadThumbnail(qualityIndex + 1);
                    return;
                }

                // 成功載入，設置封面圖
                posterImg.src = thumbnailUrl;

                // 根據質量設置 srcset
                if (quality === 'maxresdefault') {
                    const sdUrl = getYouTubeThumbnailUrl(videoId, 'sddefault');
                    const hqUrl = getYouTubeThumbnailUrl(videoId, 'hqdefault');
                    posterImg.srcset = `${thumbnailUrl} 1280w, ${sdUrl} 640w, ${hqUrl} 480w`;
                    posterImg.sizes = '(max-width: 1280px) 100vw, 1280px';
                } else if (quality === 'sddefault') {
                    const hqUrl = getYouTubeThumbnailUrl(videoId, 'hqdefault');
                    posterImg.srcset = `${thumbnailUrl} 640w, ${hqUrl} 480w`;
                    posterImg.sizes = '(max-width: 640px) 100vw, 640px';
                } else if (quality === 'hqdefault') {
                    posterImg.srcset = `${thumbnailUrl} 480w`;
                    posterImg.sizes = '(max-width: 480px) 100vw, 480px';
                } else {
                    posterImg.srcset = '';
                }

                // console.log(`✅ 已設置 YouTube 封面圖 [${quality}] (${testImg.width}x${testImg.height}):`, videoId);
            };

            testImg.onerror = function () {
                // 載入失敗，嘗試下一個質量
                // console.log(`⏭️ ${quality} 載入失敗，嘗試下一個質量... (${videoId})`);
                tryLoadThumbnail(qualityIndex + 1);
            };

            testImg.src = thumbnailUrl;
        }

        // 開始嘗試載入
        tryLoadThumbnail(currentQualityIndex);
    });
}

// 影片彈出功能
function initVideoPopup() {
    const videoPopup = document.getElementById('video-popup');
    const videoIframe = document.getElementById('video-iframe');
    const popupClose = document.querySelector('#video-popup .popup-close');

    // 點擊影片項目開啟彈出視窗 (支援首頁和 expo 頁面)
    document.addEventListener('click', function (e) {
        if (e.target.closest('.video-item')) {
            const videoItem = e.target.closest('.video-item');
            const videoUrl = videoItem.getAttribute('data-video-url');

            if (videoUrl && videoIframe && videoPopup) {
                // 轉換 YouTube URL 為嵌入格式
                const embedUrl = convertToEmbedUrl(videoUrl);
                videoIframe.src = embedUrl;
                videoPopup.classList.add('active');
                document.body.style.overflow = 'hidden'; // 防止背景滾動
            }
        }
    });

    // 關閉彈出視窗
    function closeVideoPopup() {
        if (videoPopup) {
            videoPopup.classList.remove('active');
        }
        if (videoIframe) {
            videoIframe.src = ''; // 清空 iframe src 停止影片播放
        }
        document.body.style.overflow = ''; // 恢復背景滾動
    }

    // 點擊關閉按鈕關閉彈窗
    if (popupClose) {
        popupClose.addEventListener('click', closeVideoPopup);
    }

    // 點擊背景關閉彈窗（點擊內容區域不關閉）
    if (videoPopup) {
        videoPopup.addEventListener('click', function (e) {
            if (e.target === videoPopup) {
                closeVideoPopup();
            }
        });
    }

    // 防止點擊內容區域時關閉彈窗
    const videoPopupContent = document.querySelector('#video-popup .popup-content');
    if (videoPopupContent) {
        videoPopupContent.addEventListener('click', function (e) {
            e.stopPropagation();
        });
    }

    // ESC 鍵關閉彈出視窗
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && videoPopup && videoPopup.classList.contains('active')) {
            closeVideoPopup();
        }
    });

    // 轉換 YouTube URL 為嵌入格式
    function convertToEmbedUrl(url) {
        // 處理各種 YouTube URL 格式
        let videoId = '';

        if (url.includes('youtube.com/watch?v=')) {
            videoId = url.split('v=')[1];
        } else if (url.includes('youtu.be/')) {
            videoId = url.split('youtu.be/')[1];
        } else if (url.includes('youtube.com/embed/')) {
            videoId = url.split('embed/')[1];
        }

        // 移除額外的參數
        if (videoId.includes('&')) {
            videoId = videoId.split('&')[0];
        }

        return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    }
}

// 校友彈出視窗功能
function initAlumniPopup() {
    const alumniPopup = document.getElementById('alumni-popup');
    const alumniPopupImg = document.getElementById('alumni-popup-img');
    const alumniPopupName = document.getElementById('alumni-popup-name');
    const alumniPopupTitle = document.getElementById('alumni-popup-title');
    const alumniPopupCountry = document.getElementById('alumni-popup-country');
    const alumniPopupDesc = document.getElementById('alumni-popup-desc');

    // 點擊校友卡片開啟彈出視窗
    document.addEventListener('click', function (e) {
        if (e.target.closest('.alumni-card')) {
            const alumniCard = e.target.closest('.alumni-card');

            // 提取校友資訊
            const alumniImg = alumniCard.querySelector('.alumni-avatar img');
            const alumniName = alumniCard.querySelector('.alumni-name');
            const alumniTitle = alumniCard.querySelector('.alumni-title');
            const alumniCountry = alumniCard.querySelector('.alumni-country');
            const alumniDescElements = alumniCard.querySelectorAll('.alumni-desc');

            if (alumniPopup && alumniImg && alumniName) {
                // 設置彈出視窗內容
                if (alumniPopupImg && alumniImg) {
                    alumniPopupImg.src = alumniImg.src;
                    alumniPopupImg.alt = alumniImg.alt || alumniName.textContent;
                }

                if (alumniPopupName) {
                    alumniPopupName.textContent = alumniName.textContent;
                }

                if (alumniPopupTitle && alumniTitle) {
                    alumniPopupTitle.textContent = alumniTitle.textContent;
                }

                if (alumniPopupCountry && alumniCountry) {
                    alumniPopupCountry.textContent = alumniCountry.textContent;
                }

                if (alumniPopupDesc && alumniDescElements.length > 0) {
                    // 組合所有描述段落，用 <p> 標籤包裹
                    const descHtml = Array.from(alumniDescElements)
                        .map((el) => `<p>${el.textContent.trim()}</p>`)
                        .join('');
                    alumniPopupDesc.innerHTML = descHtml;
                }

                // 顯示彈出視窗
                alumniPopup.classList.add('active');
                document.body.style.overflow = 'hidden'; // 防止背景滾動
            }
        }
    });

    // 關閉校友彈出視窗
    function closeAlumniPopup() {
        if (alumniPopup) {
            alumniPopup.classList.remove('active');
        }
        document.body.style.overflow = ''; // 恢復背景滾動
    }

    // 為校友彈出視窗的關閉按鈕和背景添加事件監聽器
    if (alumniPopup) {
        const alumniPopupClose = alumniPopup.querySelector('.popup-close');
        if (alumniPopupClose) {
            alumniPopupClose.addEventListener('click', closeAlumniPopup);
        }

        // 點擊背景關閉
        alumniPopup.addEventListener('click', function (e) {
            if (e.target === alumniPopup) {
                closeAlumniPopup();
            }
        });
    }

    // ESC 鍵關閉校友彈出視窗
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && alumniPopup && alumniPopup.classList.contains('active')) {
            closeAlumniPopup();
        }
    });
}

// Pathway 升學管道 popup 功能
// 方案 C: 從 HTML 讀取內容 (不使用 AJAX)
function initPathwayPopup() {
    const pathwayPopup = document.getElementById('pathway-popup');
    const pathwayBtns = document.querySelectorAll('.pathway-btn');

    if (!pathwayPopup || pathwayBtns.length === 0) {
        return;
    }

    // 點擊 Pathway 按鈕開啟 popup
    pathwayBtns.forEach((btn) => {
        btn.addEventListener('click', function () {
            const pathwayId = this.getAttribute('data-pathway-id');
            openPathwayPopup(pathwayId);
        });
    });

    // 關閉 popup
    const popupClose = pathwayPopup.querySelector('.popup-close');
    if (popupClose) {
        popupClose.addEventListener('click', closePathwayPopup);
    }

    // 點擊背景關閉 popup
    pathwayPopup.addEventListener('click', function (e) {
        if (e.target === pathwayPopup) {
            closePathwayPopup();
        }
    });

    // ESC 鍵關閉 popup
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && pathwayPopup.classList.contains('active')) {
            closePathwayPopup();
        }
    });

    // 開啟 Pathway popup (從 HTML 讀取內容)
    function openPathwayPopup(pathwayId) {
        // 從 DOM 中查找對應的隱藏內容
        const pathwayContent = document.querySelector(`.pathway-content[data-pathway-id="${pathwayId}"]`);

        if (!pathwayContent) {
            console.error('找不到 Pathway 內容:', pathwayId);
            showErrorState();
            return;
        }

        // 取得標題和內容
        const title = pathwayContent.querySelector('.pathway-title');
        const body = pathwayContent.querySelector('.pathway-body');

        // 填入 popup
        const popupTitle = pathwayPopup.querySelector('.pathway-popup-title');
        const popupContent = pathwayPopup.querySelector('.pathway-popup-content');

        if (popupTitle && title) {
            popupTitle.textContent = title.textContent;
        }

        if (popupContent && body) {
            // 複製完整的 HTML 內容（包含樣式和連結）
            popupContent.innerHTML = body.innerHTML;
        }

        // 顯示 popup
        pathwayPopup.classList.add('active');
        document.body.style.overflow = 'hidden';
    }

    // 關閉 Pathway popup
    function closePathwayPopup() {
        pathwayPopup.classList.remove('active');
        document.body.style.overflow = '';
    }

    // 顯示錯誤狀態
    function showErrorState() {
        const title = pathwayPopup.querySelector('.pathway-popup-title');
        const content = pathwayPopup.querySelector('.pathway-popup-content');

        if (title) title.textContent = '載入失敗';
        if (content)
            content.innerHTML = '<div style="text-align: center; padding: 20px; color: #999;">無法載入內容</div>';

        pathwayPopup.classList.add('active');
        document.body.style.overflow = 'hidden';
    }
}

// 文章彈出視窗功能
function initArticlePopup() {
    const articlePopup = document.getElementById('article-popup');

    if (!articlePopup) return;

    let articleRequest = 0;
    let activeArticleController;

    const scrollTargets = () => [articlePopup, ...articlePopup.querySelectorAll(
        '.popup-container, .popup-content, .article-popup-info, #article-popup-content'
    )];
    window.NCNArticlePopup = {
        getState() {
            if (!articlePopup.classList.contains('active') || !articlePopup.dataset.articleId) return null;
            return {
                articleId: Number(articlePopup.dataset.articleId),
                scroll: scrollTargets().map(node => ({top: node.scrollTop, left: node.scrollLeft})),
            };
        },
        async restore(state) {
            if (!Number.isSafeInteger(state.articleId) || state.articleId < 1) return;
            let interrupted = false;
            const events = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
            const interrupt = event => {
                if (!event?.target?.closest?.('.ncn-country-dialog')) interrupted = true;
            };
            events.forEach(type => window.addEventListener(type, interrupt, {capture: true, passive: true}));
            const position = () => {
                if (interrupted || !articlePopup.classList.contains('active') || Number(articlePopup.dataset.articleId) !== state.articleId) return;
                scrollTargets().forEach((node, index) => {
                    const saved = Array.isArray(state.scroll) && state.scroll[index];
                    if (!saved) return;
                    node.scrollTop = Number.isFinite(saved.top) ? saved.top : 0;
                    node.scrollLeft = Number.isFinite(saved.left) ? saved.left : 0;
                });
            };
            let image, timer;
            const cleanup = () => {
                interrupted = true;
                clearTimeout(timer);
                events.forEach(type => window.removeEventListener(type, interrupt, true));
                if (image) {
                    image.removeEventListener('load', imageReady);
                    image.removeEventListener('error', imageReady);
                }
            };
            const imageReady = () => { position(); cleanup(); };
            try {
                await openArticlePopup(state.articleId);
                position();
                image = articlePopup.querySelector('#article-popup-img');
                if (image && !image.complete && !interrupted) {
                    // 僅在相關圖片載入時微調；不等待圖片、不拖住收藏或畫面呈現。
                    image.addEventListener('load', imageReady, {once: true});
                    image.addEventListener('error', imageReady, {once: true});
                    timer = setTimeout(cleanup, 1200);
                } else cleanup();
            } catch (error) {
                cleanup();
                throw error;
            }
        },
    };

    // 點擊文章項目開啟彈出視窗
    document.addEventListener('click', function (e) {
        // 只有點擊特定元素才觸發 popup
        const clickedElement = e.target.closest(
            '.article-item .image-container, .article-item h3, .article-item .read-more'
        );

        if (clickedElement) {
            const articleItem = clickedElement.closest('.article-item');
            if (articleItem) {
                e.preventDefault();
                const articleId = articleItem.getAttribute('data-article-id');
                if (articleId) {
                    openArticlePopup(articleId);
                }
            }
        }
    });

    // 關閉彈出視窗
    function closeArticlePopup() {
        articleRequest++;
        if (activeArticleController) activeArticleController.abort();
        if (articlePopup) {
            articlePopup.classList.remove('active');
        }
        document.body.style.overflow = '';
    }

    // 點擊關閉按鈕
    const popupClose = articlePopup.querySelector('.popup-close');
    if (popupClose) {
        popupClose.addEventListener('click', closeArticlePopup);
    }

    // 點擊背景關閉
    articlePopup.addEventListener('click', function (e) {
        if (e.target === articlePopup) {
            closeArticlePopup();
        }
    });

    // ESC 鍵關閉
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && articlePopup && articlePopup.classList.contains('active')) {
            closeArticlePopup();
        }
    });

    // 開啟文章 popup
    function openArticlePopup(articleId) {
        const request = ++articleRequest;
        if (activeArticleController) activeArticleController.abort();
        articlePopup.dataset.articleId = String(articleId);
        articlePopup.classList.add('active');
        document.body.style.overflow = 'hidden';

        // 顯示載入狀態
        showArticleLoading();

        // 透過 AJAX 取得文章內容
        const controller = new AbortController();
        activeArticleController = controller;
        const timeout = setTimeout(() => controller.abort(), 10000);
        return fetch(window.ncnFavoriteResume?.ajaxUrl || '/wp-admin/admin-ajax.php', {
            method: 'POST',
            signal: controller.signal,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: 'action=get_article_details&article_id=' + encodeURIComponent(articleId),
        })
            .then((response) => response.json())
            .then((data) => {
                if (request !== articleRequest || !articlePopup.classList.contains('active')) return;
                if (data.success) {
                    populateArticlePopup(data.data);
                } else {
                    showArticleError();
                }
            })
            .catch((error) => {
                if (request !== articleRequest || !articlePopup.classList.contains('active')) return;
                console.error('Article AJAX 錯誤:', error);
                showArticleError();
            })
            .finally(() => clearTimeout(timeout));
    }

    // 顯示載入狀態
    function showArticleLoading() {
        const title = document.getElementById('article-popup-title');
        const meta = document.getElementById('article-popup-meta');
        const content = document.getElementById('article-popup-content');
        const img = document.getElementById('article-popup-img');

        if (title) title.textContent = '載入中...';
        if (meta) meta.textContent = '';
        if (content) content.innerHTML = '';
        if (img) img.src = '';
    }

    // 填入文章內容
    function populateArticlePopup(articleData) {
        const title = document.getElementById('article-popup-title');
        const meta = document.getElementById('article-popup-meta');
        const content = document.getElementById('article-popup-content');
        const img = document.getElementById('article-popup-img');

        if (title) title.textContent = articleData.title || '';
        if (meta) meta.innerHTML = articleData.meta || '';
        if (meta) jQuery(document).trigger('ncn:favorite-buttons-added', [meta]);
        if (content) content.innerHTML = articleData.content || '';
        if (img && articleData.image) {
            img.src = articleData.image;
            img.alt = articleData.title || '';
        }
    }

    // 顯示錯誤訊息
    function showArticleError() {
        const title = document.getElementById('article-popup-title');
        const meta = document.getElementById('article-popup-meta');
        const content = document.getElementById('article-popup-content');

        if (title) title.textContent = '載入失敗';
        if (meta) meta.textContent = '';
        if (content) content.innerHTML = '<p style="text-align: center; color: #999;">無法載入文章內容</p>';
    }
}

// 文章內容 popup 功能 (for short-films page)
function initArticleContentPopup() {
    // 檢查是否在 short-films 頁面
    const isShortFilmsPage =
        document.body.classList.contains('page-template-short-films') ||
        window.location.pathname.includes('short-films');

    if (!isShortFilmsPage) {
        return;
    }

    // 使用更早的事件捕獲來避免被其他事件處理器攔截
    document.addEventListener(
        'click',
        function (e) {
            // 檢查是否點擊了 Read More 按鈕且在 short-films 頁面
            const button = e.target.closest('.wp-block-button__link');
            const shortFilmsPage = e.target.closest('.page-template-short-films');

            if (button && shortFilmsPage) {
                const articleContainer = button.closest('.kt-inside-inner-col');

                if (articleContainer) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    openArticleContentPopup(articleContainer);
                    return false;
                }
            }
        },
        true
    );

    // 開啟文章內容 popup
    function openArticleContentPopup(articleContainer) {
        // 創建 popup HTML 結構
        let popup = document.getElementById('short-film-popup');
        if (!popup) {
            popup = createArticleContentPopup();
            document.body.appendChild(popup);
        }

        // 提取內容
        const image = articleContainer.querySelector('img');
        const title = articleContainer.querySelector('h3');
        const allPs = articleContainer.querySelectorAll('p:not(.video-desc)');
        const description = articleContainer.querySelector('.video-desc');

        // 創建所有非 video-desc 的 p 標籤 HTML
        let allPsHtml = '';
        allPs.forEach((p) => {
            allPsHtml += `<p>${p.textContent.trim()}</p>`;
        });

        // 填充 popup 內容
        const popupImg = popup.querySelector('#short-film-popup-img');
        const popupTitle = popup.querySelector('#short-film-popup-title');
        const popupMeta = popup.querySelector('#short-film-popup-meta');
        const popupContent = popup.querySelector('#short-film-popup-content');

        if (popupImg && image) {
            popupImg.src = image.src;
            popupImg.alt = image.alt || (title ? title.textContent : '') || '';
        }

        if (popupTitle && title) {
            popupTitle.textContent = title.textContent;
        }

        if (popupMeta) {
            popupMeta.innerHTML = allPsHtml;
        }

        if (popupContent && description) {
            popupContent.innerHTML = `<p>${description.textContent}</p>`;
        }

        // 顯示 popup
        popup.classList.add('active');
        document.body.style.overflow = 'hidden';
    }

    // 創建 popup HTML 結構
    function createArticleContentPopup() {
        const popup = document.createElement('div');
        popup.id = 'short-film-popup';
        popup.className = 'popup-overlay';
        popup.innerHTML = `
            <div class="popup-container">
                <button class="popup-close"></button>
                <div class="article-popup-body">
                    <div class="article-popup-image image-container">
                        <img id="short-film-popup-img" src="" alt="">
                    </div>
                    <div class="article-popup-info popup-content">
                        <h3 id="short-film-popup-title"></h3>
                        <div id="short-film-popup-meta" class="article-popup-meta"></div>
                        <div id="short-film-popup-content" class="article-popup-content"></div>
                    </div>
                </div>
            </div>
        `;

        // 添加事件監聽器
        const popupClose = popup.querySelector('.popup-close');
        if (popupClose) {
            popupClose.addEventListener('click', closeArticleContentPopup);
        }

        popup.addEventListener('click', function (e) {
            if (e.target === popup) {
                closeArticleContentPopup();
            }
        });

        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && popup.classList.contains('active')) {
                closeArticleContentPopup();
            }
        });

        return popup;
    }

    // 關閉 popup
    function closeArticleContentPopup() {
        const popup = document.getElementById('short-film-popup');
        if (popup) {
            popup.classList.remove('active');
        }
        document.body.style.overflow = '';
    }
}

// 註冊好處 popup 功能
function initBenefitsPopup() {
    const benefitsPopup = document.getElementById('register-benefits-popup');
    const helpIcon = document.querySelector('.help-icon');
    const popupClose = document.querySelector('#register-benefits-popup .popup-close');

    if (!benefitsPopup || !helpIcon) return;

    // 點擊問號圖示開啟 popup
    helpIcon.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        benefitsPopup.classList.add('active');
        document.body.style.overflow = 'hidden';
    });

    // 關閉 popup
    function closeBenefitsPopup() {
        benefitsPopup.classList.remove('active');
        document.body.style.overflow = '';
    }

    // 點擊關閉按鈕
    if (popupClose) {
        popupClose.addEventListener('click', closeBenefitsPopup);
    }

    // 點擊背景關閉
    benefitsPopup.addEventListener('click', function (e) {
        if (e.target === benefitsPopup) {
            closeBenefitsPopup();
        }
    });

    // 防止點擊內容區域時關閉 popup
    const popupContent = benefitsPopup.querySelector('.popup-container');
    if (popupContent) {
        popupContent.addEventListener('click', function (e) {
            e.stopPropagation();
        });
    }

    // ESC 鍵關閉
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && benefitsPopup.classList.contains('active')) {
            closeBenefitsPopup();
        }
    });

    // 立即註冊按鈕
    const registerBtn = benefitsPopup.querySelector('.btn-primary');
    if (registerBtn) {
        registerBtn.addEventListener('click', function () {
            closeBenefitsPopup();
            // 切換到註冊表單
            const registerTab = document.querySelector('button[data-tab="register"]');
            if (registerTab) {
                registerTab.click();
            }
        });
    }
}

// Gravity Form popup 功能（首頁或一般頁面使用）
function initGravityFormPopup() {
    const gformPopup = document.getElementById('gform-popup');
    const openGformBtn = document.getElementById('open-gform-popup');

    if (!gformPopup || !openGformBtn) {
        return;
    }

    // 點擊「搶先機」按鈕開啟 popup
    openGformBtn.addEventListener('click', function (e) {
        e.preventDefault();
        openGformPopup();
    });

    // 關閉 popup
    const popupClose = gformPopup.querySelector('.popup-close');
    if (popupClose) {
        popupClose.addEventListener('click', closeGformPopup);
    }

    // 點擊背景關閉 popup
    gformPopup.addEventListener('click', function (e) {
        if (e.target === gformPopup) {
            closeGformPopup();
        }
    });

    // ESC 鍵關閉 popup
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && gformPopup.classList.contains('active')) {
            closeGformPopup();
        }
    });

    // 開啟 Gravity Form popup
    function openGformPopup() {
        gformPopup.classList.add('active');
        document.body.style.overflow = 'hidden';
    }

    // 關閉 Gravity Form popup
    function closeGformPopup() {
        gformPopup.classList.remove('active');
        document.body.style.overflow = '';
    }

    // 監聽表單提交成功事件（Gravity Forms 會觸發 gform_confirmation_loaded 事件）
    jQuery(document).on('gform_confirmation_loaded', function (event, formId) {
        if (formId === 1) {
            // 表單提交成功後 3 秒自動關閉 popup
            setTimeout(closeGformPopup, 3000);
        }
    });
}

// 學校頁面專用的 Gravity Form popup 功能
function initSchoolGravityFormPopup() {
    const schoolGformPopup = document.getElementById('school-gform-popup');
    const openSchoolGformBtns = document.querySelectorAll('.open-school-gform-popup');

    if (!schoolGformPopup || openSchoolGformBtns.length === 0) {
        return;
    }

    // 點擊「E 諮詢」按鈕開啟 popup
    openSchoolGformBtns.forEach((btn) => {
        btn.addEventListener('click', function (e) {
            e.preventDefault();
            const schoolId = this.getAttribute('data-school-id');
            const schoolName = this.getAttribute('data-school-name');
            openSchoolGformPopup(schoolId, schoolName);
        });
    });

    // 關閉 popup
    const popupClose = schoolGformPopup.querySelector('.popup-close');
    if (popupClose) {
        popupClose.addEventListener('click', closeSchoolGformPopup);
    }

    // 點擊背景關閉 popup
    schoolGformPopup.addEventListener('click', function (e) {
        if (e.target === schoolGformPopup) {
            closeSchoolGformPopup();
        }
    });

    // ESC 鍵關閉 popup
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && schoolGformPopup.classList.contains('active')) {
            closeSchoolGformPopup();
        }
    });

    // 開啟學校 Gravity Form popup
    function openSchoolGformPopup(schoolId, schoolName) {
        schoolGformPopup.classList.add('active');
        document.body.style.overflow = 'hidden';

        // 等待表單完全載入後再設置值
        setTimeout(function () {
            // 透過 label 文字找到對應的欄位
            let schoolIdField = null;
            let schoolNameField = null;

            // 找到所有 label
            const labels = schoolGformPopup.querySelectorAll('.gfield_label');
            labels.forEach(function (label) {
                const labelText = label.textContent.trim();

                // 找 School ID 欄位
                if (labelText === 'School ID') {
                    const fieldContainer = label.closest('.gfield');
                    if (fieldContainer) {
                        schoolIdField = fieldContainer.querySelector('input');
                    }
                }

                // 找 School Name 欄位
                if (labelText === 'School Name') {
                    const fieldContainer = label.closest('.gfield');
                    if (fieldContainer) {
                        schoolNameField = fieldContainer.querySelector('input');
                    }
                }
            });

            // 設置 school_id
            if (schoolIdField) {
                schoolIdField.value = schoolId;
            }

            // 設置 school_name
            if (schoolNameField) {
                schoolNameField.value = schoolName;
            }
        }, 300);
    }

    // 關閉學校 Gravity Form popup
    function closeSchoolGformPopup() {
        schoolGformPopup.classList.remove('active');
        document.body.style.overflow = '';
    }

    // 監聽表單提交成功事件
    jQuery(document).on('gform_confirmation_loaded', function (event, formId) {
        if (formId === 2) {
            // 表單提交成功後 3 秒自動關閉 popup
            setTimeout(function () {
                closeSchoolGformPopup();
            }, 3000);
        }
    });
}

// 大學系統 popup 功能
function initUniversitySystemPopup() {
    const universitySystemPopup = document.getElementById('university-system-popup');
    const universitySystemBtns = document.querySelectorAll('.university-system-btn');

    if (!universitySystemPopup || universitySystemBtns.length === 0) {
        return;
    }

    // 快取機制 - 儲存已載入的大學系統資料
    const universitySystemCache = new Map();

    // 點擊大學系統按鈕開啟 popup
    universitySystemBtns.forEach((btn) => {
        btn.addEventListener('click', function (e) {
            e.preventDefault();
            const systemIds = this.getAttribute('data-system-ids');
            openUniversitySystemPopup(systemIds);
        });
    });

    // 關閉 popup
    const popupClose = universitySystemPopup.querySelector('.popup-close');
    if (popupClose) {
        popupClose.addEventListener('click', closeUniversitySystemPopup);
    }

    // 點擊背景關閉 popup
    universitySystemPopup.addEventListener('click', function (e) {
        if (e.target === universitySystemPopup) {
            closeUniversitySystemPopup();
        }
    });

    // ESC 鍵關閉 popup
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && universitySystemPopup.classList.contains('active')) {
            closeUniversitySystemPopup();
        }
    });

    // 開啟大學系統 popup
    function openUniversitySystemPopup(systemIds) {
        universitySystemPopup.classList.add('active');
        document.body.style.overflow = 'hidden';

        // 檢查快取
        if (universitySystemCache.has(systemIds)) {
            // 從快取取得資料，立即顯示
            const cachedData = universitySystemCache.get(systemIds);
            populateUniversitySystemPopup(cachedData);
        } else {
            // 快取中沒有，顯示載入狀態
            showUniversitySystemLoading();

            // 透過 AJAX 取得所有系統下的學校
            fetch('/wp-admin/admin-ajax.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: 'action=get_university_system_schools&system_ids=' + systemIds,
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.success) {
                        // 存入快取
                        universitySystemCache.set(systemIds, data.data);
                        populateUniversitySystemPopup(data.data);
                    } else {
                        showUniversitySystemError();
                    }
                })
                .catch((error) => {
                    console.error('University System AJAX 錯誤:', error);
                    showUniversitySystemError();
                });
        }
    }

    // 關閉大學系統 popup
    function closeUniversitySystemPopup() {
        universitySystemPopup.classList.remove('active');
        document.body.style.overflow = '';
    }

    // 顯示載入狀態
    function showUniversitySystemLoading() {
        const content = universitySystemPopup.querySelector('.university-system-popup-content');
        if (content) {
            content.innerHTML = '<div style="text-align: center; padding: 20px;">載入中...</div>';
        }
    }

    // 填入大學系統 popup 內容
    function populateUniversitySystemPopup(systemsData) {
        const content = universitySystemPopup.querySelector('.university-system-popup-content');
        if (content) {
            if (systemsData && systemsData.length > 0) {
                let systemsHtml = '';
                systemsData.forEach((system) => {
                    systemsHtml += `<div class="university-system-section">`;
                    systemsHtml += `<h4 class="system-title">${system.name}</h4>`;
                    systemsHtml += `<div class="system-schools">`;

                    if (system.schools && system.schools.length > 0) {
                        system.schools.forEach((school) => {
                            systemsHtml += `<a href="${school.url}" class="university-school-item" target="_blank" rel="noreferrer noopenner">${school.title}</a>`;
                        });
                    } else {
                        systemsHtml += `<div class="no-schools">該系統下暫無學校資料</div>`;
                    }

                    systemsHtml += `</div>`;
                    systemsHtml += `</div>`;
                });
                content.innerHTML = systemsHtml;
            } else {
                content.innerHTML =
                    '<div style="text-align: center; padding: 20px; color: #999;">暫無大學系統資料</div>';
            }
        }
    }

    // 顯示錯誤狀態
    function showUniversitySystemError() {
        const content = universitySystemPopup.querySelector('.university-system-popup-content');
        if (content) {
            content.innerHTML =
                '<div style="text-align: center; padding: 20px; color: #999;">無法載入學校資料，請稍後再試</div>';
        }
    }
}

// 初始化所有 popup 功能
function initAllPopups() {
    initVideoPopup();
    initAlumniPopup();
    initPathwayPopup();
    initArticlePopup();
    initArticleContentPopup();
    initBenefitsPopup();
    initUniversitySystemPopup();
    initGravityFormPopup();
    initSchoolGravityFormPopup();

    // 自動設置 YouTube 封面圖
    autoSetYouTubeThumbnails();
}

// 當 DOM 載入完成後初始化
console.log('Popup.js loaded successfully');

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllPopups);
} else {
    initAllPopups();
}
